import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import type { ShareContentType } from '../share-criteria-types.ts';

const EXPERIMENT_NAME = 'twcg-2055-confluence-onboarding-flag-to-share';

export type FlagToShareEnrollmentStatus = 'unchecked' | 'checking' | 'eligible' | 'ineligible';

export type UseFlagToShareEnrollmentParams = {
	type: ShareContentType;
};

export type UseFlagToShareEnrollmentResult = {
	status: FlagToShareEnrollmentStatus;
	checkEligibility: () => Promise<boolean>;
};

/** Owns the one-time lazy enrollment boundary for Flag-to-Share. */
export function useFlagToShareEnrollment({
	type,
}: UseFlagToShareEnrollmentParams): UseFlagToShareEnrollmentResult {
	const [cachedStatus, setCachedStatus] = useState<FlagToShareEnrollmentStatus>('unchecked');
	const cachedStatusRef = useRef<FlagToShareEnrollmentStatus>('unchecked');
	const activeTypeRef = useRef(type);
	const isMountedRef = useRef(false);
	const inFlightCheckRef = useRef<Promise<boolean> | null>(null);
	const status = type === 'slides' ? 'ineligible' : cachedStatus;

	useLayoutEffect(() => {
		activeTypeRef.current = type;
	}, [type]);

	useLayoutEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
			inFlightCheckRef.current = null;
		};
	}, []);

	const checkEligibility = useCallback((): Promise<boolean> => {
		if (activeTypeRef.current === 'slides') {
			return Promise.resolve(false);
		}

		if (cachedStatusRef.current === 'eligible') {
			return Promise.resolve(true);
		}
		if (cachedStatusRef.current === 'ineligible') {
			return Promise.resolve(false);
		}

		const inFlightCheck = inFlightCheckRef.current;
		if (inFlightCheck) {
			return inFlightCheck;
		}

		cachedStatusRef.current = 'checking';
		setCachedStatus('checking');
		const promise = Promise.resolve()
			.then(() => {
				// The query-backed audience check will run here before the experiment check.
				if (!isMountedRef.current) {
					return false;
				}

				return isExperimentEnabled(EXPERIMENT_NAME);
			})
			.catch(() => false)
			.then((isEligible) => {
				if (isMountedRef.current) {
					const nextStatus = isEligible ? 'eligible' : 'ineligible';
					cachedStatusRef.current = nextStatus;
					setCachedStatus(nextStatus);
				}
				return isEligible;
			})
			.finally(() => {
				if (inFlightCheckRef.current === promise) {
					inFlightCheckRef.current = null;
				}
			});

		inFlightCheckRef.current = promise;
		return promise;
	}, []);

	return { status, checkEligibility };
}
